import { FunnelRevenueResult, FunnelStage } from "models/opportunity-dashboard"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import ZoneCard from "./zone-card"
import { compactMoney, exactMoney } from "./format"

interface FunnelRevenueTabProps {
    data: FunnelRevenueResult
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const periodLabel = (packed: number | null) =>
    packed === null ? "No closed period yet" : `Through ${calendar.fiscalToLabel(Math.floor(packed / 100), packed % 100)}`

// Funnel shape: fixed widths narrowing step by step, like the Power BI visual (not sized by value).
const FUNNEL_WIDTHS = [100, 83, 67, 50, 33, 17]

const stageTone = (stage: string) => {
    const s = stage.toLowerCase()
    if (s.startsWith("identified") || s.startsWith("engaged")) return "blue"
    if (s.startsWith("won")) return "green"
    return "yellow"
}

// Grid rows of the Stages / Sales Funnel panel: row 1 is the titles, rows 2-7 the six stages.
// Blue covers Identified + Engaged, Yellow the three proposal stages, Green is Won.
const ZONE_ROWS = { blue: "2 / 4", yellow: "4 / 7", green: "7 / 8" }
const ARROW_ROWS = [2, 4, 7]

const stageTitle = (s: FunnelStage, fiscalYear: number) =>
    `${s.stage}: ${exactMoney(s.value)} · ${s.count} opp. closing in FY ${fiscalYear}`

// Power BI layout: revenue + funnel health on the left, stages and sales funnel on the right.
const FunnelRevenueTab = ({ data }: FunnelRevenueTabProps) => {
    const { zones, revenue, fiscalYear } = data

    const steps = [
        { badge: String(fiscalYear), label: "Revenue Earned YTD", note: periodLabel(revenue.earnedThroughPeriod), value: revenue.earnedYtd },
        { badge: String(fiscalYear), label: "Work in Hand for Remaining of Year", value: revenue.workInHandRemaining },
        { badge: `${revenue.nextFiscalYear}+`, label: "Carryover Revenue", value: revenue.carryover },
    ]

    return <div className="funnel-revenue">
        <div className="funnel-revenue-health">
            <div className="funnel-revenue-banner">Revenue + Funnel Health</div>

            <div className="funnel-revenue-headline">
                <div title={exactMoney(revenue.fyRevenue)}><strong>FY {fiscalYear} Revenue:</strong> {compactMoney(revenue.fyRevenue)}</div>
                <div>
                    <span title={exactMoney(revenue.revenueTarget)}><strong>Revenue Target:</strong> {compactMoney(revenue.revenueTarget)}</span>
                    <span
                        className={`ms-4 ${revenue.gap < 0 ? "text-danger" : "text-success"}`}
                        title={`${exactMoney(revenue.gap)} (earned + work in hand − target)`}
                    >
                        <strong>GAP:</strong> {compactMoney(revenue.gap)}
                    </span>
                </div>
                <div title={`${exactMoney(revenue.totalFunnel)} (blue + yellow zones)`}>
                    <strong>Total Funnel Value:</strong> {compactMoney(revenue.totalFunnel)}
                </div>
            </div>

            <div className="funnel-revenue-steps">
                {steps.map((step, i) => [
                    <div key={`b${i}`} className="funnel-revenue-step-badge" style={{ gridColumn: i + 2, gridRow: i * 2 + 1 }}>
                        {step.badge}
                    </div>,
                    <div key={`l${i}`} className="funnel-revenue-step-label" style={{ gridColumn: i + 1, gridRow: i * 2 + 2 }}>
                        {step.label}
                        {step.note && <small className="d-block text-muted">{step.note}</small>}
                    </div>,
                    <div key={`v${i}`} className="funnel-revenue-step-value" style={{ gridColumn: i + 2, gridRow: i * 2 + 2 }}>
                        {exactMoney(step.value)}
                    </div>,
                ])}
            </div>
        </div>

        <div className="funnel-revenue-divider" />

        <div className="funnel-revenue-stages">
            <h5 className="funnel-revenue-title" style={{ gridColumn: "1 / 3" }}>Stages</h5>
            <h5 className="funnel-revenue-title" style={{ gridColumn: "3 / 5" }}>Sales Funnel</h5>

            <ZoneCard
                tone="blue"
                title="Blue Zone 5-7X"
                description="Identified opportunities at early stage of development."
                zone={zones.blue}
                style={{ gridRow: ZONE_ROWS.blue }}
            />
            <ZoneCard
                tone="yellow"
                title="Yellow Zone 3-5X"
                description="Represents opportunities where RAM has delivered a proposal or has been shortlisted, or contract is in negotiation."
                zone={zones.yellow}
                style={{ gridRow: ZONE_ROWS.yellow }}
            />
            <ZoneCard
                tone="green"
                title="Green Zone"
                zone={zones.green}
                style={{ gridRow: ZONE_ROWS.green }}
            />

            {ARROW_ROWS.map(row => <div key={row} className="funnel-revenue-arrow" style={{ gridRow: row }} />)}

            {data.funnel.map((s, i) => [
                <div key={`l${s.stage}`} className="funnel-revenue-stage-label" style={{ gridRow: i + 2 }}>
                    {s.stage} ({Math.round(s.weight * 100)}%)
                </div>,
                <div key={`b${s.stage}`} className="funnel-revenue-bar-track" style={{ gridRow: i + 2 }}>
                    <div
                        className={`funnel-revenue-bar funnel-revenue-bar-${stageTone(s.stage)}`}
                        style={{ width: `${FUNNEL_WIDTHS[i] ?? FUNNEL_WIDTHS[FUNNEL_WIDTHS.length - 1]}%` }}
                        title={stageTitle(s, fiscalYear)}
                    />
                </div>,
            ])}
        </div>
    </div>
}

export default FunnelRevenueTab
